import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';

import { requestConfirmBuyPasses } from 'network/services/payments.service';
import {
  readPendingConektaOrderId,
  clearPendingConektaOrderId,
} from 'features/payments/utils/conekta-pending-order.util';

// Rescata la compra cuando el componente 3DS de Conekta NO corrió en modo embebido.
//
// En ese caso no emite `postMessage`: hace `window.top.location.href = urlRedirect`,
// que navega la ventana entera a la `return_url` con `?payment_status=...`. El modal
// y su sondeo se destruyen con la navegación, así que sin esto la compra quedaría
// cobrada y sin pases hasta que el usuario recargara.
//
// Es un respaldo, no el camino principal: el flujo normal es embebido. Y como todo lo
// demás, no confía en el query param — solo lo usa como señal para preguntarle al
// backend, que re-consulta la orden contra Conekta.
//
// Se monta una vez en App.js, así que funciona sea cual sea la `return_url`
// configurada en CONEKTA_3DS_RETURN_URL.
export const useConektaRedirectReturn = () => {
  const { search } = useLocation();
  const { replace } = useHistory();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // El efecto depende de `search`, que cambia al limpiar la URL; esto evita
  // reentrar mientras la confirmación sigue en vuelo.
  const isHandlingRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const paymentStatus = params.get('payment_status');

    if (paymentStatus === null || isHandlingRef.current) return;

    const orderId = readPendingConektaOrderId();

    // Limpia el query param siempre, haya o no orden pendiente, para que recargar no
    // vuelva a disparar esto.
    const cleanUrl = () => {
      params.delete('payment_status');
      const rest = params.toString();
      replace({ search: rest ? `?${rest}` : '' });
    };

    if (!orderId) {
      cleanUrl();
      return;
    }

    isHandlingRef.current = true;

    requestConfirmBuyPasses({ orderId })
      .then(response => {
        // Conekta pide otra verificación: no se puede re-presentar el reto después de
        // haber perdido la ventana, así que se le dice al usuario en vez de fingir
        // éxito. La orden sigue viva y el webhook la cerrará si llega a pagarse.
        if (response && response.requiresAction) {
          enqueueSnackbar(
            'La verificación quedó pendiente. Revisa tus pases antes de volver a intentar',
            {
              variant: 'warning',
            },
          );
          return;
        }

        clearPendingConektaOrderId();
        queryClient.invalidateQueries('GET_PASSES');
        enqueueSnackbar('Se realizó la compra con éxito', {
          variant: 'success',
        });
      })
      .catch(() => {
        clearPendingConektaOrderId();
        enqueueSnackbar('No se pudo confirmar el pago. Intenta más tarde', {
          variant: 'error',
        });
      })
      .finally(() => {
        isHandlingRef.current = false;
        cleanUrl();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
};
